/**
 * "You" — the admin's own account, the way the student and teacher apps have
 * one. Same shared `SettingsLayout`, so the three consoles read alike.
 *
 * Two tabs and a persistent Logout. Profile is the admin acting on THEMSELVES,
 * which is neither `auth/profile` (that route demands a student or teacher
 * persona and refuses a pure admin) nor `console/users/{id}` (the authority to
 * administer someone else) — hence `console/me`.
 *
 * Diagnostics answers "what am I actually running": which build, pointed at
 * which backend, carrying which gates. The things you want when one console is
 * behaving unlike the one next to it.
 *
 * See docs/FeatureDesigns/admin-you-area-current.md.
 */
import { Fragment, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { SettingsLayout, type SettingsTabDef, Card, CardHeader, CardContent, TextField } from '@shared-ui/components';
import { isWebPopupEnabled, setWebPopupEnabled } from '@shared-ui/logging';
import { authApi, API_BASE } from '../api/auth';
import { profileApi } from '../api/profileApi';
import { useAdminUser } from '../hooks/useAdminUser';
import './You.css';

type TabId = 'profile' | 'diagnostics';

const TABS: SettingsTabDef[] = [
  { id: 'profile', label: 'Profile', icon: '👤' },
  { id: 'diagnostics', label: 'Diagnostics', icon: '⚙️' },
];

export function You() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const sessionUser = useAdminUser();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab') as TabId | null;
  const activeTab: TabId = TABS.some(t => t.id === tabParam) ? (tabParam as TabId) : 'profile';
  const setActiveTab = (id: string) => setSearchParams({ tab: id }, { replace: true });

  const { data: profile, isLoading, isError } = useQuery({
    queryKey: ['my-profile'],
    queryFn: () => profileApi.get(),
    retry: false,
  });
  // The server decides this, not the client: it is the one that knows whether a
  // user record exists behind this token. The platform super-admin signs in
  // against a seeded credential and has none — a state, not a failure.
  const editable = !!profile?.editable;

  // Seeded from the profile the moment it arrives, by adjusting state during
  // render rather than in an effect — an effect would paint the empty fields
  // first and then replace them, and the form has no other reason to render
  // twice. Keyed on the id so it seeds once and never fights the typing.
  const [name, setName] = useState('');
  const [shortName, setShortName] = useState('');
  const [seededFor, setSeededFor] = useState<string | null>(null);
  const seeded = !!profile && seededFor === profile.id;
  if (profile && seededFor !== profile.id) {
    setSeededFor(profile.id);
    setName(profile.name ?? '');
    setShortName(profile.shortName ?? '');
  }

  const [saved, setSaved] = useState(false);
  const save = useMutation({
    mutationFn: () => profileApi.update({ name: name.trim(), shortName: shortName.trim() || undefined }),
    onSuccess: (updated) => {
      // The saved values become the new baseline. Without this `dirty` keeps
      // comparing against what loaded, so the form stays dirty after a
      // successful save and the confirmation never appears. Set rather than
      // invalidated: the response IS the new record, so refetching it would be
      // a round trip to learn what we were just told. The id is unchanged, so
      // the render-time seeding does not re-fire.
      queryClient.setQueryData(['my-profile'], updated);
      // The sidebar reads the CACHED session user, not this query — without
      // this the name it shows stays as it was at login.
      authApi.updateUser({ name: updated.name ?? name.trim() });
      setSaved(true);
    },
  });

  // Saved-confirmation is transient; any further edit retracts it rather than
  // leaving "Saved" sitting above changed fields.
  const onEdit = (setter: (v: string) => void) => (v: string) => { setSaved(false); setter(v); };
  const dirty = seeded && (name.trim() !== (profile?.name ?? '') || shortName.trim() !== (profile?.shortName ?? ''));

  const [errorPopups, setErrorPopups] = useState(() => isWebPopupEnabled());
  const toggleErrorPopups = () => {
    const next = !errorPopups;
    setErrorPopups(next);
    setWebPopupEnabled(next);
  };

  const gateEntries = useMemo(
    () => Object.entries(sessionUser?.featureGates ?? {}).sort(([a], [b]) => a.localeCompare(b)),
    [sessionUser],
  );

  const roleLabel = useMemo(() => {
    if (sessionUser?.isSuperAdmin) return 'Super admin';
    const assigned = sessionUser?.roleAssignments?.map(a => `${a.role} @ ${a.scope}`);
    if (assigned?.length) return assigned.join(', ');
    return sessionUser?.roles?.join(', ') || '—';
  }, [sessionUser]);

  return (
    <div className="you-page">
      <SettingsLayout
        title="You"
        tabs={TABS}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        navFooter={
          <button
            className="you-logout"
            onClick={() => { authApi.logout(); navigate('/login'); }}
          >
            Logout
          </button>
        }
      >
        {activeTab === 'profile' && (
          <>
            <Card padding="none">
              <CardHeader title="Your details" />
              <CardContent>
                {isLoading && <p className="you-muted">Loading…</p>}

                {!isLoading && editable && (
                  <>
                    <TextField
                      label="Name"
                      required
                      value={name}
                      onChange={e => onEdit(setName)(e.target.value)}
                      placeholder="Your full name"
                    />
                    <TextField
                      label="Short name"
                      infoTip="What you'd rather be called. Optional."
                      value={shortName}
                      onChange={e => onEdit(setShortName)(e.target.value)}
                      placeholder="e.g. Vinay"
                    />
                    <div className="you-actions">
                      <button
                        className="you-save"
                        onClick={() => save.mutate()}
                        disabled={!dirty || !name.trim() || save.isPending}
                      >
                        {save.isPending ? 'Saving…' : 'Save'}
                      </button>
                      {saved && !dirty && <span className="you-saved">Saved</span>}
                      {save.isError && (
                        <span className="you-error">
                          {save.error instanceof Error ? save.error.message : 'Could not save'}
                        </span>
                      )}
                    </div>
                  </>
                )}

                {!isLoading && !editable && (
                  <p className="you-muted">
                    {sessionUser?.isSuperAdmin
                      ? 'The super-admin signs in against a platform credential rather than a user record, so there is no profile to edit here.'
                      : isError
                        ? 'Your profile could not be loaded, so it cannot be edited right now.'
                        : 'This sign-in has no editable profile.'}
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Identity, not settings. Everything here is decided elsewhere —
                shown so an admin can read their own authority without asking
                someone to look it up. */}
            <Card padding="none">
              <CardHeader title="Account" />
              <CardContent>
                <dl className="you-facts">
                  <dt>Email</dt><dd>{sessionUser?.email || profile?.email || '—'}</dd>
                  <dt>User ID</dt><dd className="you-mono">{sessionUser?.id || profile?.id || '—'}</dd>
                  <dt>Roles</dt><dd>{roleLabel}</dd>
                  <dt>Tenant</dt><dd className="you-mono">{sessionUser?.tenantId || '—'}</dd>
                </dl>
              </CardContent>
            </Card>
          </>
        )}

        {activeTab === 'diagnostics' && (
          <>
            <Card padding="none">
              <CardHeader title="Notifications" />
              <CardContent>
                <div className="you-row">
                  <div className="you-row-info">
                    <span className="you-row-label">Show error pop-ups</span>
                    <span className="you-row-desc">Toast messages for errors and warnings, top-right of the screen. Per device.</span>
                  </div>
                  <label className="you-switch">
                    <input type="checkbox" checked={errorPopups} onChange={toggleErrorPopups} />
                    <span className="you-slider" />
                  </label>
                </div>
              </CardContent>
            </Card>

            {/* Read from the session, not from the shared local-state panel:
                that panel reads a localStorage cache only the student app
                writes, so here it would render an empty box. These are the
                gates the login response actually resolved for this account —
                what the console is running on, which is the question. Signing
                in again is what changes them. */}
            <Card padding="none">
              <CardHeader title="Feature gates" />
              <CardContent>
                {gateEntries.length === 0 ? (
                  <p className="you-muted">No gates resolved for this account.</p>
                ) : (
                  <dl className="you-facts">
                    {gateEntries.map(([gate, on]) => (
                      <Fragment key={gate}>
                        <dt className="you-mono">{gate}</dt>
                        <dd className={on ? 'you-gate-on' : 'you-gate-off'}>{on ? 'on' : 'off'}</dd>
                      </Fragment>
                    ))}
                  </dl>
                )}
              </CardContent>
            </Card>

            <Card padding="none">
              <CardHeader title="This build" />
              <CardContent>
                <dl className="you-facts">
                  <dt>API</dt><dd className="you-mono">{API_BASE}</dd>
                  <dt>Built</dt><dd>{format(new Date(__BUILD_TIMESTAMP__), 'MMM d, yyyy HH:mm:ss')}</dd>
                  <dt>Ring</dt><dd>{profile?.ring || '—'}</dd>
                </dl>
              </CardContent>
            </Card>
          </>
        )}
      </SettingsLayout>
    </div>
  );
}
